import { createContext, forwardRef, useContext, type ComponentProps, type ElementRef } from 'react'
import cn from 'clsx'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import type { TTabsContentProps, TTabsListProps, TTabsTriggerProps } from './Tabs.types'
import { DEFAULT_TABS_ID_PREFIX, getTabContentId, getTabTriggerId } from './tabsIds'
import styles from './Tabs.module.css'

/**
 * Radix derives trigger/content ids from `useId()`, which cannot be reproduced by the SSR template —
 * so the ids come from the tab value instead, and `Tabs.server` emits the same ones. Values have to be
 * unique within a page; a second instance can namespace itself with `idPrefix`.
 */
const IdPrefixContext = createContext(DEFAULT_TABS_ID_PREFIX)

type TTabsProps = ComponentProps<typeof TabsPrimitive.Root> & { idPrefix?: string }

export const Tabs = ({ idPrefix = DEFAULT_TABS_ID_PREFIX, ...props }: TTabsProps) => (
    <IdPrefixContext.Provider value={idPrefix}>
        <TabsPrimitive.Root {...props} />
    </IdPrefixContext.Provider>
)

export const TabsList = forwardRef<ElementRef<typeof TabsPrimitive.List>, TTabsListProps>(
    ({ className, ...props }, ref) => (
        <TabsPrimitive.List ref={ref} className={cn(styles.list, className)} {...props} />
    ),
)

export const TabsTrigger = forwardRef<ElementRef<typeof TabsPrimitive.Trigger>, TTabsTriggerProps>(
    ({ className, value, ...props }, ref) => {
        const prefix = useContext(IdPrefixContext)

        return (
            <TabsPrimitive.Trigger
                ref={ref}
                value={value}
                id={getTabTriggerId(prefix, value)}
                aria-controls={getTabContentId(prefix, value)}
                className={cn(styles.trigger, className)}
                {...props}
            />
        )
    },
)

export const TabsContent = forwardRef<ElementRef<typeof TabsPrimitive.Content>, TTabsContentProps>(
    ({ className, value, ...props }, ref) => {
        const prefix = useContext(IdPrefixContext)

        return (
            <TabsPrimitive.Content
                ref={ref}
                value={value}
                id={getTabContentId(prefix, value)}
                aria-labelledby={getTabTriggerId(prefix, value)}
                className={cn(styles.content, className)}
                {...props}
            />
        )
    },
)
